import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Placeholder from "@/components/Placeholder";
import { RevealImage, AssetPlaceholder } from "@/components/RevealImage";

const Lineage = () => {
  return (
    <>
      <SEO path="/lineage" />

      <section className="bg-ivory" data-testid="lineage-hero">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-16 md:pb-24">
          <p className="eyebrow">Lineage</p>
          <h1 className="mt-5 font-serif font-light text-temple text-5xl md:text-7xl lg:text-[5.25rem] leading-[0.92] max-w-4xl">
            The story<br/>
            <span className="text-stone italic">of the source.</span>
          </h1>
          <p className="mt-6 font-sans font-light text-temple text-base md:text-lg leading-[1.7] max-w-prose">
            Two paths — Receive and Become — resolve into one source. This is
            where the source is named.
          </p>
        </div>
      </section>

      <section className="bg-ivory" data-testid="lineage-portrait">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 pb-16 md:pb-24">
          <figure
            className="w-full max-w-5xl mx-auto"
            data-testid="lineage-ratu-nabe-portrait"
          >
            <RevealImage
              src="/assets/ratu-melukat-priest.png"
              alt="Ratu Nabe at melukat with his priest and the deities"
              style={{ aspectRatio: "1 / 1" }}
              imgClassName="w-full h-full object-contain"
              loading="eager"
              fetchpriority="high"
            />
            <figcaption
              className="mt-6 font-sans font-light text-stone text-sm tracking-[0.04em] text-center"
              data-testid="lineage-ratu-nabe-caption"
            >
              Ratu Nabe.
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="bg-ivory" data-testid="lineage-text-1">
        <div className="max-w-3xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
          <p className="eyebrow">Ratu Nabe</p>
          <h2
            className="mt-8 font-serif font-light text-temple leading-[1.1] tracking-[0.005em]"
            style={{ fontSize: "clamp(2rem, 4vw, 3.6rem)" }}
            data-testid="lineage-ratu-nabe-headline"
          >
            Before the temple,<br />there was the ring.
          </h2>
          <div
            className="mt-10 font-sans font-light text-temple text-base md:text-lg leading-[1.9] space-y-6 max-w-prose"
            data-testid="lineage-ratu-nabe-body"
          >
            <p>
              Ratu Nabe came of age building strength the way the modern world
              teaches — through bodybuilding, through boxing, through the
              discipline of the fist and the muscle. It was a language he
              understood. The call to heal was not.
            </p>
            <p>
              For years, he refused it. The call came anyway — quietly, then
              not quietly at all. When he finally fell deathly ill,
              with no explanation the body or the doctors could offer, it was
              not medicine that returned him to health. It was surrender to
              the thing he had spent a lifetime denying. He rose from that
              illness a healer, initiated not by choice but by consequence —
              the oldest way the lineage has ever chosen its keepers.
            </p>
            <p>What followed was not a career. It was a return.</p>
            <p>
              From that reckoning, he founded Shiva Murti Bali, now a
              defining force in the healing life of Gianyar and a wellspring
              for the modality's growth across Bali and around the world. His
              clinic draws patients by the hundreds — on its busiest days, as
              many as two hundred pass through in a single day, each seeking
              what medicine alone could not give him.
            </p>
            <p>
              He does not describe himself as having chosen this path.
              The path chose him, tested him, and only then let him serve.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-ivory" data-testid="lineage-portrait-2">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 items-start">
            <figure
              className="w-full"
              data-testid="lineage-ratu-istra-portrait"
            >
              <RevealImage
                src="/assets/ratu-nabe.png"
                alt="Ratu Nabe and Ratu Istra"
                style={{ aspectRatio: "1 / 1" }}
                imgClassName="w-full h-full object-cover"
              />
              <figcaption
                className="mt-6 font-sans font-light text-stone text-sm tracking-[0.04em]"
                data-testid="lineage-ratu-istra-caption"
              >
                Ratu Nabe and Ratu Istra.
              </figcaption>
            </figure>
            <figure
              className="w-full"
              data-testid="lineage-melukat-portrait"
            >
              <RevealImage
                src="/assets/melukat-blessing.png"
                alt="The practitioner being blessed by Ratu Nabe at his private melukat temple"
                style={{ aspectRatio: "1 / 1" }}
                imgClassName="w-full h-full object-cover"
              />
              <figcaption
                className="mt-6 font-sans font-light text-stone text-sm tracking-[0.04em]"
                data-testid="lineage-melukat-caption"
              >
                The practitioner being blessed by Ratu Nabe at his private melukat temple.
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Line of Transmission — quiet three-row index */}
      <section className="bg-ivory" data-testid="lineage-transmission">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="eyebrow text-center">Line of transmission</p>
          <ol className="mt-16 border-t border-gold" data-testid="transmission-list">
            <li
              className="border-b border-temple/15 py-10 md:py-12 grid grid-cols-[auto_1fr] gap-6 md:gap-10 items-baseline"
              data-testid="transmission-ratu-nabe"
            >
              <span className="font-sans text-[0.7rem] tracking-[0.28em] uppercase text-gold">01</span>
              <div>
                <h3 className="font-serif font-light text-temple text-2xl md:text-3xl leading-[1.2]">
                  Ratu Nabe
                </h3>
                <p className="mt-3 font-sans font-light text-stone text-sm md:text-base leading-[1.8] max-w-prose">
                  High priest · creator of the lineage
                </p>
              </div>
            </li>
            <li
              className="border-b border-temple/15 py-10 md:py-12 grid grid-cols-[auto_1fr] gap-6 md:gap-10 items-baseline"
              data-testid="transmission-jero-jason"
            >
              <span className="font-sans text-[0.7rem] tracking-[0.28em] uppercase text-gold">02</span>
              <div>
                <h3 className="font-serif font-light text-temple text-2xl md:text-3xl leading-[1.2]">
                  Jero Jason
                </h3>
                <p className="mt-3 font-sans font-light text-stone text-sm md:text-base leading-[1.8] max-w-prose">
                  Initiated Shiva Murti teacher · received the lineage directly from Ratu Nabe
                </p>
              </div>
            </li>
            <li
              className="py-10 md:py-12 grid grid-cols-[auto_1fr] gap-6 md:gap-10 items-baseline"
              data-testid="transmission-johnny"
            >
              <span className="font-sans text-[0.7rem] tracking-[0.28em] uppercase text-gold">03</span>
              <div>
                <h3 className="font-serif font-light text-temple text-2xl md:text-3xl leading-[1.2]">
                  Johnny Cantiveros
                </h3>
                <p className="mt-3 font-sans font-light text-stone text-sm md:text-base leading-[1.8] max-w-prose">
                  Teacher within the Shiva Murti Bali lineage
                </p>
              </div>
            </li>
          </ol>
          <hr className="hairline" />
        </div>
      </section>

      {/* Teacher and student — Johnny with Jero Jason */}
      <section className="bg-ivory" data-testid="lineage-teacher-student">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
          <figure
            className="w-full max-w-3xl mx-auto"
            data-testid="lineage-jero-johnny-portrait"
          >
            <RevealImage
              src="/assets/jero-johnny.png"
              alt="With my teacher, Jero Jason — an initiated Shiva Murti teacher who received the lineage directly from Ratu Nabe"
              style={{ aspectRatio: "1 / 1" }}
              imgClassName="w-full h-full object-cover"
            />
            <figcaption
              className="mt-6 font-sans font-light text-stone text-sm tracking-[0.04em] text-center max-w-prose mx-auto"
              data-testid="lineage-jero-johnny-caption"
            >
              With my teacher, Jero Jason — the door through which this practice reached me.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Texture break — saput poleng textile (duality, structural) */}
      <section className="bg-ivory" data-testid="lineage-texture">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-0 flex justify-center">
          <img
            src="/assets/saput-poleng.webp"
            alt="Saput poleng — Balinese black-and-white ritual cloth"
            className="block w-full max-w-4xl h-auto select-none"
            loading="lazy"
            decoding="async"
            style={{ mixBlendMode: "multiply" }}
            data-testid="lineage-texture-poleng"
          />
        </div>
      </section>


      {/* Two paths resolve back into one source — polar-opposite doors */}
      <section className="bg-ivory" data-testid="lineage-resolve">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 pt-4 md:pt-8 pb-0 text-center">
          <p className="eyebrow" data-testid="resolve-eyebrow">The two paths, one source</p>
          <h2 className="mt-6 font-serif font-light text-temple text-3xl md:text-5xl leading-[1.1] max-w-3xl mx-auto">
            Choose the door that calls you.
          </h2>
        </div>

        <div
          className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-[1fr_1px_1fr] gap-0"
          data-testid="resolve-doors"
        >
          {/* LEFT — RECEIVE: Temple Black band */}
          <article
            className="bg-temple text-ivory px-8 md:px-16 lg:px-24 py-16 md:py-24 flex flex-col items-center text-center"
            data-testid="resolve-receive-door"
          >
            <p className="eyebrow">Receive</p>
            <h3 className="mt-4 font-serif font-light text-ivory text-4xl md:text-5xl leading-[0.95]">
              Healing &amp;<br />ceremony.
            </h3>
            <div className="mt-auto pt-10">
              <Link
                to="/receive"
                className="inline-flex items-center gap-3 px-7 py-4 bg-ivory text-temple border border-gold font-sans text-[0.72rem] tracking-[0.28em] uppercase hover:bg-temple hover:text-ivory transition-colors duration-500"
                data-testid="resolve-receive"
              >
                Enter
                <span aria-hidden>→</span>
              </Link>
            </div>
          </article>

          {/* Sacred-Gold Poleng seam */}
          <div aria-hidden className="hidden md:block w-px bg-gold" />
          <div aria-hidden className="md:hidden h-px w-full bg-gold" />

          {/* RIGHT — BECOME: Ivory band (polar opposite) */}
          <article
            className="bg-ivory text-temple px-8 md:px-16 lg:px-24 py-16 md:py-24 flex flex-col items-center text-center"
            data-testid="resolve-become-door"
          >
            <p className="eyebrow">Become</p>
            <h3 className="mt-4 font-serif font-light text-temple text-4xl md:text-5xl leading-[0.95]">
              Initiation into<br />the teaching.
            </h3>
            <div className="mt-auto pt-10">
              <Link
                to="/become"
                className="inline-flex items-center gap-3 px-7 py-4 bg-temple text-ivory border border-gold font-sans text-[0.72rem] tracking-[0.28em] uppercase hover:bg-ivory hover:text-temple transition-colors duration-500"
                data-testid="resolve-become"
              >
                Enter
                <span aria-hidden>→</span>
              </Link>
            </div>
          </article>
        </div>
      </section>
    </>
  );
};

export default Lineage;
